import { useState } from 'react';
import { Check, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface SaveContactFormProps {
    /** The number being saved — shown as a caption under the name input. */
    readonly phone: string;
    readonly isSaving: boolean;
    readonly onSave: (name: string) => void;
    readonly onCancel: () => void;
    /** Unique input id — two mounts (post-call card + history detail) must not collide. */
    readonly idPrefix: string;
}

/**
 * The inline save-a-number-as-contact mini-form, shared by the post-call
 * wrap-up card and the History detail view. Owns only the typed name; the
 * caller owns the mutation (and closes the form on success).
 */
export function SaveContactForm({
    phone,
    isSaving,
    onSave,
    onCancel,
    idPrefix,
}: SaveContactFormProps) {
    const [name, setName] = useState('');

    const handleSave = (): void => {
        const trimmed = name.trim();
        if (!trimmed) return;
        onSave(trimmed);
    };

    return (
        <div
            data-slot="voice-save-contact-form"
            className="flex flex-col gap-2 rounded-xl border border-white/50 bg-white/40 p-2.5 dark:border-white/10 dark:bg-white/[0.04]"
        >
            <Label htmlFor={`${idPrefix}-contact-name`} required>
                Name
            </Label>
            <Input
                id={`${idPrefix}-contact-name`}
                autoFocus
                placeholder="Contact name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') handleSave();
                }}
            />
            <p className="truncate text-xs text-text-tertiary" title={phone}>
                {phone}
            </p>
            <div className="flex gap-2">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    disabled={isSaving}
                    onClick={onCancel}
                >
                    Cancel
                </Button>
                <Button
                    type="button"
                    size="sm"
                    className="flex-1 gap-1.5"
                    disabled={isSaving || !name.trim()}
                    onClick={handleSave}
                >
                    {isSaving ? (
                        <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                        <Check className="size-3.5" />
                    )}
                    Save
                </Button>
            </div>
        </div>
    );
}
